import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { getSubscriptionPrices } from "../../../api/services/subscription";
import { usePropsDataStore } from "../store/PropsDataStore";
import {
  mapPropertiesData,
  getOrderFromCookie,
  clearOrderFromCookie,
} from "../comps/SubscriptionCheckout/utils";
import { calculatePrices } from "../comps/SubscriptionCheckout/helpers";

const SubscriptionContext = createContext();

export const SubscriptionCheckoutContext = ({
  children,
  totalSteps = 3,
  isOpen = false,
}) => {
  const { props } = usePropsDataStore();

  const [currentStep, setCurrentStep] = useState(1);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(isOpen);
  const [stepCount, setStepCount] = useState(totalSteps);

  const [userProperties, setUserProperties] = useState([]);
  const [subscriptionPrices, setSubscriptionPrices] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);

  const [selectedSubscription, setSelectedSubscription] = useState([]);

  const [couponDiscount, setCouponDiscount] = useState(undefined);
  const [extraData, setExtraData] = useState({});

  const [orderPrices, setOrderPrices] = useState({
    discountPrice: undefined,
    fullPrice: undefined,
    propertiesTotalPrice: undefined,
    propertiesHectarePrice: undefined,
    tax: undefined,
    MultiPlusDiscount: undefined
  });

  const [paymentMethod, setPaymentMethod] = useState(undefined);

  const [openSelectPropertyDialog, setOpenSelectPropertyDialog] = useState(false);
  const [alertDialog, setAlertDialog] = useState(null);

  // Track whether user was logged in when checkout opened
  const [startedAsGuest, setStartedAsGuest] = useState(false);

  const hasPlusAccess = useMemo(() => props.some((p) => p.dfr), [props]);

  useEffect(() => {
    if (isOpen) setIsCheckoutOpen(true);
  }, [isOpen]);

  // Memoize functions to prevent unnecessary re-renders
  const openCheckout = useCallback((options) => {
    
    if (options?.step) {
      setCurrentStep(options.step);

      // Restore order data from cookie when opening directly at the last step (e.g. Klarna redirect)
      if (options.step === stepCount) {
        const order = getOrderFromCookie();
        if (order) {
          setOrderPrices(order.orderPrices);
          setPaymentMethod(order.paymentMethod);
          setCouponDiscount(order.couponDiscount);
          setSelectedProperties(order.selectedProperties);
          setExtraData(order.extraData);
          clearOrderFromCookie();
        }
      }
    }
    setIsCheckoutOpen(true);
  }, [stepCount]);

  useEffect(() => {
    // Only run when checkout is open
    if (!isCheckoutOpen) return;

    const fetchSubscriptionPrices = async () => {
      const { ok, status, data } = await getSubscriptionPrices();
      if (ok) {
        setSubscriptionPrices(data);
      } else {
        console.error("Price fetch error: ", status, data);
      }
    };

    fetchSubscriptionPrices();
  }, [isCheckoutOpen]);

  
  useEffect(() => {
    if (!isCheckoutOpen) return;
    // merging store props with owned_props from react_data to get the necessary data for purchasing.
    const propOptions = [...(Window.react_data?.owned_props ?? [])];
    props.forEach((storeProp) => {
      const alreadyListed = propOptions.some(
        (existingProp) => existingProp.ref === storeProp.ref
      );
      if (!alreadyListed) {
        propOptions.push(storeProp);
      }
    });
    setUserProperties(mapPropertiesData(propOptions));
  }, [isCheckoutOpen, props]);

  useEffect(() => {
    if (!isCheckoutOpen) return;

    const purchasedPropRefs = selectedProperties.map((sp) => sp.ref)

    const hasDfr = props.some((p) => p.dfr && !purchasedPropRefs.includes(p.ref))

    if (selectedProperties.length > 0) {
      const priceCalculation = calculatePrices({
        properties: selectedProperties,
        couponDiscount,
        hasDfr
      });

      setPaymentMethod("klarna");

      setOrderPrices((prev) => ({
        ...prev,
        discountPrice: priceCalculation.discountedPrice,
        fullPrice: priceCalculation.fullPrice,
        tax: 0.25 * priceCalculation.discountedPrice,
        propertiesHectarePrice: priceCalculation.propertiesHectarePrice,
        propertiesTotalPrice: priceCalculation.propertiesTotalPrice,
        MultiPlusDiscount: priceCalculation.MultiPlusDiscount,
      }));
    } else {
      setOrderPrices((prev) => ({
        ...prev,
        discountPrice: undefined,
        fullPrice: undefined,
        MultiPlusDiscount: undefined,
      }));
    }
  }, [isCheckoutOpen, selectedProperties, couponDiscount]);

  

  const goToStep = useCallback(
    (step) => {
      if (step >= 1 && step <= stepCount) setCurrentStep(step);
    },
    [stepCount]
  );
  const closeCheckout = useCallback(() => setIsCheckoutOpen(false), []);

  const addSelectedProperty = useCallback(
    (property) => {
      setSelectedProperties((prev) => {
        if (prev.some((p) => p.ref === property.ref)) return prev;
        return [
          ...prev,
          {
            ...property,
            forestArea: Math.round(property?.forestArea ?? 0),
          },
        ];
      });
    },
    []
  );

  const removeSelectedProperty = useCallback(
    (property) =>
      setSelectedProperties((prev) =>
        prev.filter((p) => p.ref !== property.ref)
      ),
    []
  );

  const setCurrentStepValue = useCallback(
    (step) =>
      step >= 1 && step <= stepCount ? setCurrentStep(step) : currentStep,
    [currentStep, stepCount]
  );

  const nextStep = useCallback(() => {
    if (currentStep === stepCount) {
      closeCheckout();
      resetCheckout();
      return;
    }

    setCurrentStepValue(currentStep + 1);
  }, [currentStep, setCurrentStepValue, stepCount]);
  const previousStep = useCallback(
    () => setCurrentStepValue(currentStep - 1),
    [currentStep, setCurrentStepValue]
  );

  const resetCheckout = useCallback(() => {
    closeCheckout();
    setCurrentStep(1);
    setSelectedProperties([]);

    setAlertDialog(null);
    setExtraData({});
    setCouponDiscount(undefined);
    setPaymentMethod(undefined);
    setStartedAsGuest(false);
    setOrderPrices({
      discountPrice: undefined,
      fullPrice: undefined,
      propertiesTotalPrice: undefined,
      propertiesHectarePrice: undefined,
      tax: undefined,
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      isCheckoutOpen,
      openCheckout,
      closeCheckout,

      // Property
      userProperties,
      addSelectedProperty,
      removeSelectedProperty,
      selectedProperties,

      // Subscription
      selectedSubscription,
      setSelectedSubscription,

      // Coupon discount
      couponDiscount,
      setCouponDiscount,

      // Extra data
      extraData,
      setExtraData,

      // Step
      currentStep,
      stepCount,
      setStepCount,
      goToStep,
      nextStep,
      previousStep,

      // Order prices
      orderPrices,
      setOrderPrices,

      // Payment method
      paymentMethod,
      setPaymentMethod,

      // Property prices
      subscriptionPrices,

      // Reset checkout
      resetCheckout,

      // Property select dialog trigger
      openSelectPropertyDialog,
      setOpenSelectPropertyDialog,

      // Alert dialog
      alertDialog,
      setAlertDialog,

      // Guest checkout tracking
      startedAsGuest,
      setStartedAsGuest,

      // Plus access
      hasPlusAccess,
    }),
    [
      isCheckoutOpen,
      openCheckout,
      closeCheckout,
      userProperties,
      addSelectedProperty,
      removeSelectedProperty,
      selectedProperties,
      couponDiscount,
      setCouponDiscount,
      extraData,
      setExtraData,
      currentStep,
      stepCount,
      setStepCount,
      goToStep,
      nextStep,
      previousStep,
      orderPrices,
      setOrderPrices,
      resetCheckout,
      subscriptionPrices,
      paymentMethod,
      setPaymentMethod,
      openSelectPropertyDialog,
      setOpenSelectPropertyDialog,
      alertDialog,
      setAlertDialog,
      startedAsGuest,
      setStartedAsGuest,
      hasPlusAccess,
    ]
  );
  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error(
      "useSubscription must be used within a SubscriptionProvider"
    );
  }
  return context;
};

export const useSubscriptionOptional = () => useContext(SubscriptionContext);

export default SubscriptionContext;
